import { PALETTE, FONT_STACK } from "./primitives.js";
export const FIELDS = [
    {
        key: "primary",
        path: "primary",
        cssVar: "--coras-brand-primary",
        kind: "color",
        brandValue: "var(--coras-palette-primary-500)",
        default: PALETTE.primary[500],
        themeVar: true,
        control: true,
        property: true,
        group: "Key colours",
        label: "Primary Brand Colour",
        description: "Used on key brand identifiers: buttons, ratings stars, selected states",
    },
    {
        key: "secondary",
        path: "secondary",
        cssVar: "--coras-brand-secondary",
        kind: "color",
        brandValue: "var(--coras-palette-secondary-500)",
        default: PALETTE.secondary[500],
        themeVar: true,
        control: true,
        property: true,
        group: "Key colours",
        label: "Secondary Brand Colour",
        description: "Used on supporting brand elements and secondary buttons",
    },
    {
        key: "pageBackground",
        path: "page.background",
        cssVar: "--coras-brand-page-background",
        kind: "color",
        brandValue: "var(--coras-palette-white)",
        default: PALETTE.white,
        darkValue: "#101216",
        themeVar: true,
        control: true,
        property: true,
        group: "Key colours",
        label: "Page Background Colour",
        description: "The main page background behind all content; transparent by default so the embed adopts the host page background",
    },
    {
        key: "text",
        path: "page.text",
        cssVar: "--coras-brand-text",
        kind: "color",
        brandValue: "var(--coras-palette-secondary-500)",
        default: PALETTE.secondary[500],
        darkValue: "#ffffff",
        themeVar: true,
        control: true,
        property: false,
        group: "Key colours",
        label: "Body Text Colour",
        description: "All body copy; auto-suggested to stay readable on the page background",
    },
    {
        key: "textMuted",
        path: "page.textMuted",
        cssVar: "--coras-brand-text-muted",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-600)",
        default: PALETTE.neutral[600],
        darkValue: "#cccccc",
        themeVar: true,
        control: true,
        property: false,
        group: "Key colours",
        label: "Muted Text Colour",
        description: "Field labels, placeholders, captions and hints; auto-suggested as a softened blend of text and background",
    },
    {
        key: "link",
        path: "page.link",
        cssVar: "--coras-brand-link",
        kind: "color",
        brandValue: "var(--coras-brand-text-button)",
        default: PALETTE.primary[500],
        themeVar: true,
        control: true,
        property: true,
        group: "Key colours",
        label: "Link Text Colour",
        description: "This follows the Text Button Colour, which itself defaults to the Primary Brand Colour. Adjust here if desired.",
    },
    {
        key: "icon",
        path: "page.icon",
        cssVar: "--coras-brand-icon",
        kind: "color",
        brandValue: "var(--coras-brand-text)",
        default: PALETTE.secondary[500],
        themeVar: true,
        control: true,
        property: true,
        group: "Key colours",
        label: "Icon Colour (In Page)",
        description: "This defaults to the Body Text Colour but can be adjusted here if desired",
    },
    {
        key: "focusRing",
        path: "page.focusRing",
        cssVar: "--coras-brand-focus-ring",
        kind: "color",
        brandValue: "var(--coras-brand-primary)",
        default: PALETTE.primary[500],
        themeVar: true,
        control: false,
        property: true,
        group: "Key colours",
        label: "Keyboard focus ring",
        description: "The outline shown around controls when navigating with a keyboard",
    },
    {
        key: "scrim",
        path: "page.scrim",
        cssVar: "--coras-brand-scrim",
        kind: "color",
        brandValue: "var(--coras-palette-black)",
        default: PALETTE.black,
        themeVar: true,
        control: false,
        property: false,
        group: "Key colours",
        label: "Overlay dim",
        description: "The darkened backdrop behind dialogs, drawers and popups",
    },
    {
        key: "cardBackground",
        path: "card.background",
        cssVar: "--coras-brand-card-background",
        kind: "color",
        brandValue: "var(--coras-palette-primary-50)",
        default: PALETTE.primary[50],
        darkValue: "#191c22",
        themeVar: true,
        control: true,
        property: true,
        group: "Cards & panels",
        label: "Card Fill Colour",
        description: "Cards, panels, and the price/options section backgrounds",
    },
    {
        key: "cardBorder",
        path: "card.border",
        cssVar: "--coras-brand-card-border",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-200)",
        default: PALETTE.neutral[200],
        darkValue: PALETTE.neutral[800],
        themeVar: true,
        control: true,
        property: false,
        group: "Cards & panels",
        label: "Card Border Colour",
        description: "Borders around cards and panels",
    },
    {
        key: "fieldBackground",
        path: "field.background",
        cssVar: "--coras-brand-field-background",
        kind: "color",
        brandValue: "var(--coras-brand-page-background)",
        default: PALETTE.white,
        darkValue: "#101216",
        themeVar: true,
        control: true,
        property: true,
        group: "Cards & panels",
        label: "Field / Input Fill Colour",
        description: "The inside of text fields, dropdown triggers, the card fields on the payment page, and the ticket, time and language selection controls",
    },
    {
        key: "fieldBorder",
        path: "field.border",
        cssVar: "--coras-brand-field-border",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-400)",
        default: PALETTE.neutral[400],
        darkValue: PALETTE.neutral[500],
        themeVar: true,
        control: true,
        property: false,
        group: "Cards & panels",
        label: "Field / Input Border Colour",
        description: "Borders of text fields, dropdown triggers and other form inputs",
    },
    {
        key: "menuBackground",
        path: "menu.background",
        cssVar: "--coras-brand-menu-background",
        kind: "color",
        brandValue: "var(--coras-brand-page-background)",
        default: PALETTE.white,
        // The page background, as in light mode: a menu panel that sat a step
        // lighter read as a stray block wherever one renders inline rather than
        // floating, such as the calendar on a narrow screen. `menuBorder` and the
        // shadow separate a floating panel from what it covers.
        darkValue: "#101216",
        themeVar: true,
        control: true,
        property: true,
        group: "Cards & panels",
        label: "Menu / Dropdown Fill Colour",
        description: "The panel behind dropdown menus: the basket, currency, language, sort by, location results and the calendar",
    },
    {
        key: "menuBorder",
        path: "menu.border",
        cssVar: "--coras-brand-menu-border",
        kind: "color",
        brandValue: "var(--coras-brand-card-border)",
        default: PALETTE.neutral[200],
        themeVar: true,
        control: true,
        property: false,
        group: "Cards & panels",
        label: "Menu / Dropdown Border Colour",
        description: "The outline around a dropdown menu panel",
    },
    {
        key: "divider",
        path: "page.divider",
        cssVar: "--coras-brand-divider",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-200)",
        default: PALETTE.neutral[200],
        darkValue: PALETTE.neutral[900],
        themeVar: true,
        control: true,
        property: false,
        group: "Cards & panels",
        label: "Dividing Lines",
        description: "Hairline rules between sections and list items, on the web and in emails",
    },
    {
        key: "cardShadow",
        path: "card.shadow",
        cssVar: "--coras-brand-card-shadow",
        kind: "shadow",
        brandValue: "0px 24px 24px rgba(0, 0, 0, 0.04)",
        default: "0px 24px 24px rgba(0, 0, 0, 0.04)",
        themeVar: true,
        control: true,
        property: false,
        group: "Cards & panels",
        label: "Card Shadow",
        description: "The large soft shadow under cards (set to none for a flat look)",
    },
    {
        key: "softShadow",
        path: "card.softShadow",
        cssVar: "--coras-brand-soft-shadow",
        kind: "shadow",
        brandValue: "0px 4px 4px rgba(0, 0, 0, 0.04)",
        default: "0px 4px 4px rgba(0, 0, 0, 0.04)",
        themeVar: true,
        control: true,
        property: false,
        group: "Cards & panels",
        label: "Container Shadow",
        description: "The subtle shadow on smaller raised elements",
    },
    {
        key: "primaryButton",
        path: "primaryButton.background",
        cssVar: "--coras-brand-primary-button",
        kind: "color",
        brandValue: "var(--coras-brand-primary)",
        default: PALETTE.primary[500],
        themeVar: true,
        control: true,
        property: true,
        group: "Buttons",
        label: "Primary Button Fill Colour",
        description: "This defaults to the Primary Brand Colour but can be adjusted here if required",
    },
    {
        key: "primaryButtonText",
        path: "primaryButton.text",
        cssVar: "--coras-brand-primary-button-text",
        kind: "color",
        brandValue: PALETTE.white,
        default: PALETTE.white,
        themeVar: true,
        control: true,
        property: false,
        group: "Buttons",
        label: "Primary Button Text Colour",
        description: "There is an auto suggested value here to maintain legibility and contrast. This can be adjusted if desired.",
    },
    {
        key: "primaryButtonHover",
        path: "primaryButton.hover",
        cssVar: "--coras-brand-primary-button-hover",
        kind: "color",
        brandValue: "#3f4ebf",
        default: "#3f4ebf",
        darkValue: "#5968d8",
        themeVar: true,
        control: false,
        property: false,
        group: "Buttons",
        label: "Primary button - hover",
        description: "The primary button while the pointer hovers over it",
    },
    {
        key: "primaryButtonPressed",
        path: "primaryButton.pressed",
        cssVar: "--coras-brand-primary-button-pressed",
        kind: "color",
        brandValue: "#3947ae",
        default: "#3947ae",
        darkValue: "#6775dc",
        themeVar: true,
        control: false,
        property: false,
        group: "Buttons",
        label: "Primary button - pressed",
        description: "The primary button while it is being clicked",
    },
    {
        key: "secondaryButton",
        path: "secondaryButton.background",
        cssVar: "--coras-brand-secondary-button",
        kind: "color",
        brandValue: "var(--coras-brand-secondary)",
        default: PALETTE.secondary[500],
        themeVar: true,
        control: true,
        property: true,
        group: "Buttons",
        label: "Secondary Button Fill Colour",
        description: "This defaults to the Secondary Brand Colour but can be adjusted here if required",
    },
    {
        key: "secondaryButtonText",
        path: "secondaryButton.text",
        cssVar: "--coras-brand-secondary-button-text",
        kind: "color",
        brandValue: PALETTE.white,
        default: PALETTE.white,
        themeVar: true,
        control: true,
        property: false,
        group: "Buttons",
        label: "Secondary Button Text Colour",
        description: "There is an auto suggested value here to maintain legibility and contrast. This can be adjusted if desired.",
    },
    {
        key: "secondaryButtonHover",
        path: "secondaryButton.hover",
        cssVar: "--coras-brand-secondary-button-hover",
        kind: "color",
        brandValue: "#14193d",
        default: "#14193d",
        darkValue: "#2d3357",
        themeVar: true,
        control: false,
        property: false,
        group: "Buttons",
        label: "Secondary button - hover",
        description: "The secondary button while the pointer hovers over it",
    },
    {
        key: "secondaryButtonPressed",
        path: "secondaryButton.pressed",
        cssVar: "--coras-brand-secondary-button-pressed",
        kind: "color",
        brandValue: "#121738",
        default: "#121738",
        darkValue: "#404566",
        themeVar: true,
        control: false,
        property: false,
        group: "Buttons",
        label: "Secondary button - pressed",
        description: "The secondary button while it is being clicked",
    },
    {
        key: "textButton",
        path: "button.textButton",
        cssVar: "--coras-brand-text-button",
        kind: "color",
        brandValue: "var(--coras-brand-primary)",
        default: PALETTE.primary[500],
        darkValue: "#6279f9",
        themeVar: true,
        control: true,
        property: true,
        group: "Buttons",
        label: "Text Button Colour",
        description: "This defaults to the Primary Brand Colour. It can be adjusted here if required.",
    },
    {
        key: "primaryButtonBorder",
        path: "primaryButton.border",
        cssVar: "--coras-brand-primary-button-border",
        kind: "color",
        brandValue: "transparent",
        default: "transparent",
        themeVar: true,
        control: true,
        property: false,
        group: "Buttons",
        label: "Primary Button Border",
        description: "This defaults to Transparent/No border. You can add a border colour here if desired.",
    },
    {
        key: "secondaryButtonBorder",
        path: "secondaryButton.border",
        cssVar: "--coras-brand-secondary-button-border",
        kind: "color",
        brandValue: "transparent",
        default: "transparent",
        themeVar: true,
        control: true,
        property: false,
        group: "Buttons",
        label: "Secondary Button Border",
        description: "This defaults to Transparent/No border. You can add a border colour here if desired.",
    },
    {
        key: "disabled",
        path: "button.disabled",
        cssVar: "--coras-brand-disabled",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-500)",
        default: PALETTE.neutral[500],
        darkValue: PALETTE.neutral[400],
        themeVar: true,
        control: true,
        property: false,
        group: "Buttons",
        label: "Disabled Buttons",
        description: "Default values applied. You can adjust here if desired",
    },
    {
        key: "buttonShadow",
        path: "button.shadow",
        cssVar: "--coras-brand-button-shadow",
        kind: "shadow",
        brandValue: "0px 4px 4px rgba(0, 0, 0, 0.1)",
        default: "0px 4px 4px rgba(0, 0, 0, 0.1)",
        themeVar: true,
        control: true,
        property: false,
        group: "Buttons",
        label: "Button Shadow",
        description: "Default values applied. Set values to 0 if not required",
    },
    {
        key: "buttonHoverOnLightFill",
        path: "button.hoverOnLightFill",
        cssVar: "--coras-brand-button-hover-on-light-fill",
        kind: "length",
        brandValue: "20%",
        default: "20%",
        themeVar: true,
        control: true,
        property: false,
        group: "Buttons",
        label: "On Hover - Light / Bright Fill Button",
        description: "Strength of the black overlay applied on hover when the button fill is light. Default values applied. Adjust if desired.",
    },
    {
        key: "buttonHoverOnDarkFill",
        path: "button.hoverOnDarkFill",
        cssVar: "--coras-brand-button-hover-on-dark-fill",
        kind: "length",
        brandValue: "20%",
        default: "20%",
        themeVar: true,
        control: true,
        property: false,
        group: "Buttons",
        label: "On Hover - Dark Fill Button",
        description: "Strength of the white overlay applied on hover when the button fill is dark. Default values applied. Adjust if desired.",
    },
    {
        key: "radius",
        path: "radius",
        cssVar: "--coras-brand-radius",
        kind: "length",
        brandValue: "1.5rem",
        default: "1.5rem",
        themeVar: true,
        control: true,
        property: true,
        group: "Shape",
        label: "Corner Radius",
        description: "Defines the corner radius of Cards, Images and Containers. Buttons and chips default to this value but can be customised below if desired.",
    },
    {
        key: "buttonRadius",
        path: "button.radius",
        cssVar: "--coras-brand-button-radius",
        kind: "length",
        brandValue: "var(--coras-brand-radius)",
        default: "1.5rem",
        themeVar: true,
        control: true,
        property: true,
        group: "Shape",
        label: "Button Corners",
        description: "Corner radius for buttons (follows Corner Radius until set)",
    },
    {
        key: "chipRadius",
        path: "chip.radius",
        cssVar: "--coras-brand-chip-radius",
        kind: "length",
        brandValue: "var(--coras-brand-radius)",
        default: "1.5rem",
        themeVar: true,
        control: true,
        property: true,
        group: "Shape",
        label: "Chip Corners",
        description: "Corner radius for filter and option chips (follows Corner Radius until set)",
    },
    {
        key: "fieldRadius",
        path: "field.radius",
        cssVar: "--coras-brand-field-radius",
        kind: "length",
        brandValue: "0.5rem",
        default: "0.5rem",
        themeVar: true,
        control: true,
        property: false,
        group: "Shape",
        label: "Field / Input Corners",
        description: "Corner radius for text fields, form inputs, the landing selectors and the ticket selector. Deliberately independent of Corner Radius, so a very round brand keeps usable field shapes.",
    },
    {
        key: "searchRadius",
        path: "search.radius",
        cssVar: "--coras-brand-search-radius",
        kind: "length",
        brandValue: "100px",
        default: "100px",
        themeVar: true,
        control: true,
        property: false,
        group: "Shape",
        label: "Search Field Corners",
        description: "Corner radius for the nav search and the location search field",
    },
    {
        key: "font",
        path: "font",
        cssVar: "--coras-brand-font",
        kind: "font",
        brandValue: FONT_STACK,
        default: FONT_STACK,
        themeVar: true,
        control: true,
        property: false,
        group: "Typography",
        label: "Font",
        description: "Font for all text (add hosted files under the custom font option)",
    },
    {
        key: "headingFont",
        path: "headingFont",
        cssVar: "--coras-brand-heading-font",
        kind: "font",
        brandValue: "var(--coras-brand-font)",
        default: FONT_STACK,
        themeVar: true,
        control: true,
        property: false,
        group: "Typography",
        label: "Heading font",
        description: "Font for page, section and card headings (follows Font until set)",
    },
    {
        key: "headingScale",
        path: "headingScale",
        cssVar: "--coras-brand-heading-scale",
        kind: "scale",
        brandValue: "1",
        default: "1",
        themeVar: true,
        control: true,
        property: false,
        group: "Typography",
        label: "Heading size",
        description: "Scales every heading together: 1 is the standard size, 1.25 is 25% larger",
    },
    {
        key: "logoHeight",
        path: "logoHeight",
        cssVar: "--coras-brand-logo-height",
        kind: "length",
        brandValue: "2rem",
        default: "2rem",
        themeVar: true,
        control: true,
        property: false,
        group: "Logo",
        label: "Logo Height",
        description: "How tall the logo renders in the navbar and email header",
    },
    {
        key: "navbarBackground",
        path: "navbar.background",
        cssVar: "--coras-brand-navbar-background",
        kind: "color",
        brandValue: "var(--coras-palette-white)",
        default: PALETTE.white,
        darkValue: "#101216",
        themeVar: true,
        control: true,
        property: true,
        group: "Navigation bar",
        label: "Background Colour",
        description: "Navbar background (independent of the page background)",
    },
    {
        key: "navbarBackgroundImage",
        path: "navbar.backgroundImage",
        cssVar: "--coras-brand-navbar-background-image",
        kind: "gradient",
        brandValue: "none",
        default: "none",
        themeVar: true,
        control: true,
        property: false,
        group: "Navigation bar",
        label: "Background Gradient",
        description: "Optional gradient painted over the Background Colour on the web. The mobile menu, the border and emails keep the solid colour",
    },
    {
        key: "navbarText",
        path: "navbar.text",
        cssVar: "--coras-brand-navbar-text",
        kind: "color",
        brandValue: "var(--coras-palette-black)",
        default: PALETTE.black,
        darkValue: "#ffffff",
        themeVar: true,
        control: true,
        property: true,
        group: "Navigation bar",
        label: "Text Colour",
        description: "Navbar labels and menu text",
    },
    {
        key: "navbarIcon",
        path: "navbar.icon",
        cssVar: "--coras-brand-navbar-icon",
        kind: "color",
        brandValue: "var(--coras-brand-navbar-text)",
        default: PALETTE.black,
        themeVar: true,
        control: true,
        property: true,
        group: "Navigation bar",
        label: "Nav Bar Icon Colour",
        description: "This defaults to the Text Colour selected for the Navigation Bar but can be adjusted here if required",
    },
    {
        key: "navbarBorder",
        path: "navbar.border",
        cssVar: "--coras-brand-navbar-border",
        kind: "color",
        brandValue: "var(--coras-brand-navbar-background)",
        default: PALETTE.white,
        themeVar: true,
        control: true,
        property: true,
        group: "Navigation bar",
        label: "Bottom Border",
        description: "This defaults to the Background Colour but can be adjusted here if a trim is required",
    },
    {
        key: "navbarShadow",
        path: "navbar.shadow",
        cssVar: "--coras-brand-navbar-shadow",
        kind: "shadow",
        brandValue: "0 4px 4px 0 rgba(0, 0, 0, 0.04)",
        default: "0 4px 4px 0 rgba(0, 0, 0, 0.04)",
        themeVar: true,
        control: true,
        property: false,
        group: "Navigation bar",
        label: "Drop Shadow",
        description: "Default values applied. Set values to 0 if not required",
    },
    {
        key: "footerBackground",
        path: "footer.background",
        cssVar: "--coras-brand-footer-background",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-200)",
        default: PALETTE.neutral[200],
        darkValue: "#191c22",
        themeVar: true,
        control: true,
        property: true,
        group: "Footer",
        label: "Background Colour",
        description: "Footer background, on the web and in emails",
    },
    {
        key: "footerBackgroundImage",
        path: "footer.backgroundImage",
        cssVar: "--coras-brand-footer-background-image",
        kind: "gradient",
        brandValue: "none",
        default: "none",
        themeVar: true,
        control: true,
        property: false,
        group: "Footer",
        label: "Background Gradient",
        description: "Optional gradient painted over the Background Colour on the web. The border and emails keep the solid colour",
    },
    {
        key: "footerText",
        path: "footer.text",
        cssVar: "--coras-brand-footer-text",
        kind: "color",
        brandValue: "var(--coras-palette-black)",
        default: PALETTE.black,
        darkValue: "#ffffff",
        themeVar: true,
        control: true,
        property: true,
        group: "Footer",
        label: "Text Colour",
        description: "Footer text, on the web and in emails",
    },
    {
        key: "footerLink",
        path: "footer.link",
        cssVar: "--coras-brand-footer-link",
        kind: "color",
        brandValue: "var(--coras-brand-footer-text)",
        default: PALETTE.black,
        themeVar: true,
        control: false,
        property: true,
        group: "Footer",
        label: "Links",
        description: "Footer links (follows Footer text until set)",
    },
    {
        key: "footerBorder",
        path: "footer.border",
        cssVar: "--coras-brand-footer-border",
        kind: "color",
        brandValue: "var(--coras-brand-footer-background)",
        default: PALETTE.neutral[200],
        themeVar: true,
        control: true,
        property: false,
        group: "Footer",
        label: "Top Border",
        description: "This defaults to the Background Colour but can be adjusted here if a trim is required",
    },
    {
        key: "footerPaddingBottom",
        path: "footer.paddingBottom",
        cssVar: "--coras-brand-footer-padding-bottom",
        kind: "length",
        brandValue: "2rem",
        default: "2rem",
        themeVar: true,
        control: true,
        property: false,
        group: "Footer",
        label: "Footer Padding Bottom",
        description: "The space under the footer content, for a taller footer",
    },
    {
        key: "error",
        path: "status.error",
        cssVar: "--coras-brand-error",
        kind: "color",
        brandValue: "var(--coras-palette-error-500)",
        default: PALETTE.error[500],
        darkValue: "#f5748a",
        themeVar: true,
        control: true,
        property: true,
        group: "Alerts & status",
        label: "Error colour",
        description: "Errors, destructive actions and the Music category tag",
    },
    {
        key: "errorBackground",
        path: "status.errorBackground",
        cssVar: "--coras-brand-error-background",
        kind: "color",
        brandValue: "var(--coras-palette-error-50)",
        default: PALETTE.error[50],
        darkValue: "#442e36",
        themeVar: true,
        control: true,
        property: false,
        group: "Alerts & status",
        label: "Error background",
        description: "The pale fill behind error alerts and tags",
    },
    {
        key: "success",
        path: "status.success",
        cssVar: "--coras-brand-success",
        kind: "color",
        brandValue: "var(--coras-palette-success-500)",
        default: PALETTE.success[500],
        darkValue: "#34d8b4",
        themeVar: true,
        control: true,
        property: true,
        group: "Alerts & status",
        label: "Success colour",
        description: "Success messages and the Sport category tag",
    },
    {
        key: "successBackground",
        path: "status.successBackground",
        cssVar: "--coras-brand-success-background",
        kind: "color",
        brandValue: "var(--coras-palette-success-50)",
        default: PALETTE.success[50],
        darkValue: "#26403f",
        themeVar: true,
        control: true,
        property: false,
        group: "Alerts & status",
        label: "Success background",
        description: "The pale fill behind success alerts and tags",
    },
    {
        key: "warning",
        path: "status.warning",
        cssVar: "--coras-brand-warning",
        kind: "color",
        brandValue: "var(--coras-palette-warning-500)",
        default: PALETTE.warning[500],
        darkValue: "#f4a93c",
        themeVar: true,
        control: true,
        property: true,
        group: "Alerts & status",
        label: "Warning colour",
        description: "Warnings and the Theatre category tag",
    },
    {
        key: "warningBackground",
        path: "status.warningBackground",
        cssVar: "--coras-brand-warning-background",
        kind: "color",
        brandValue: "var(--coras-palette-warning-50)",
        default: PALETTE.warning[50],
        darkValue: "#43382e",
        themeVar: true,
        control: true,
        property: false,
        group: "Alerts & status",
        label: "Warning background",
        description: "The pale fill behind warning alerts and tags",
    },
    {
        key: "info",
        path: "status.info",
        cssVar: "--coras-brand-info",
        kind: "color",
        brandValue: "var(--coras-palette-info-500)",
        default: PALETTE.info[500],
        darkValue: "#ba86e1",
        themeVar: true,
        control: true,
        property: true,
        group: "Alerts & status",
        label: "Info colour",
        description: "Info messages, reservation timers, Activity tags and marketing tags",
    },
    {
        key: "infoBackground",
        path: "status.infoBackground",
        cssVar: "--coras-brand-info-background",
        kind: "color",
        brandValue: "var(--coras-palette-info-50)",
        default: PALETTE.info[50],
        darkValue: "#373045",
        themeVar: true,
        control: true,
        property: false,
        group: "Alerts & status",
        label: "Info background",
        description: "The pale fill behind info alerts and Activity tags",
    },
    {
        key: "primaryTint",
        path: "primaryTint",
        cssVar: "--coras-brand-primary-tint",
        kind: "color",
        brandValue: "var(--coras-palette-primary-50)",
        default: PALETTE.primary[50],
        darkValue: "#141823",
        themeVar: true,
        control: false,
        property: false,
        group: "Alerts & status",
        label: "Primary tint",
        description: "The pale primary fill behind selected chips and highlights",
    },
    {
        key: "secondaryTint",
        path: "secondaryTint",
        cssVar: "--coras-brand-secondary-tint",
        kind: "color",
        brandValue: "var(--coras-palette-secondary-50)",
        default: PALETTE.secondary[50],
        darkValue: "#10131a",
        themeVar: true,
        control: false,
        property: false,
        group: "Alerts & status",
        label: "Secondary tint",
        description: "The pale secondary fill behind selected options",
    },
    {
        key: "loyaltyBackground",
        path: "loyalty.background",
        cssVar: "--coras-brand-loyalty-background",
        kind: "color",
        brandValue: "var(--coras-brand-secondary-tint)",
        default: PALETTE.secondary[50],
        darkValue: "#10131a",
        themeVar: true,
        control: true,
        property: false,
        group: "Loyalty",
        label: "Loyalty badge background",
        description: "The fill behind the loyalty programme badge (miles or points earned) on cards, the details page and the basket; follows the secondary tint by default",
    },
    {
        key: "loyaltyText",
        path: "loyalty.text",
        cssVar: "--coras-brand-loyalty-text",
        kind: "color",
        brandValue: "var(--coras-brand-text)",
        default: PALETTE.secondary[500],
        darkValue: "#ffffff",
        themeVar: true,
        control: true,
        property: false,
        group: "Loyalty",
        label: "Loyalty badge text",
        description: "The amount and unit text on the loyalty programme badge; follows the body text colour by default",
    },
    {
        key: "neutral50",
        path: "neutral.50",
        cssVar: "--coras-brand-neutral-50",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-50)",
        default: PALETTE.neutral[50],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 50 (lightest)",
        description: "Near-white: skeletons and sunken areas",
    },
    {
        key: "neutral100",
        path: "neutral.100",
        cssVar: "--coras-brand-neutral-100",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-100)",
        default: PALETTE.neutral[100],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 100",
        description: "Very light grey fills",
    },
    {
        key: "neutral200",
        path: "neutral.200",
        cssVar: "--coras-brand-neutral-200",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-200)",
        default: PALETTE.neutral[200],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 200",
        description: "Light grey fills and empty states",
    },
    {
        key: "neutral300",
        path: "neutral.300",
        cssVar: "--coras-brand-neutral-300",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-300)",
        default: PALETTE.neutral[300],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 300",
        description: "Soft borders and inactive elements",
    },
    {
        key: "neutral400",
        path: "neutral.400",
        cssVar: "--coras-brand-neutral-400",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-400)",
        default: PALETTE.neutral[400],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 400",
        description: "Mid-light grey lines",
    },
    {
        key: "neutral500",
        path: "neutral.500",
        cssVar: "--coras-brand-neutral-500",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-500)",
        default: PALETTE.neutral[500],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 500",
        description: "Mid grey: inactive icons and controls",
    },
    {
        key: "neutral600",
        path: "neutral.600",
        cssVar: "--coras-brand-neutral-600",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-600)",
        default: PALETTE.neutral[600],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 600",
        description: "Mid-dark grey text",
    },
    {
        key: "neutral700",
        path: "neutral.700",
        cssVar: "--coras-brand-neutral-700",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-700)",
        default: PALETTE.neutral[700],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 700",
        description: "Dark grey text",
    },
    {
        key: "neutral800",
        path: "neutral.800",
        cssVar: "--coras-brand-neutral-800",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-800)",
        default: PALETTE.neutral[800],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 800",
        description: "Very dark grey",
    },
    {
        key: "neutral900",
        path: "neutral.900",
        cssVar: "--coras-brand-neutral-900",
        kind: "color",
        brandValue: "var(--coras-palette-neutral-900)",
        default: PALETTE.neutral[900],
        themeVar: true,
        control: false,
        property: false,
        group: "Greys",
        label: "Grey 900 (darkest)",
        description: "Near-black",
    },
    {
        key: "logo",
        path: "logo",
        cssVar: "",
        kind: "asset",
        brandValue: "",
        default: "",
        themeVar: false,
        control: false,
        property: false,
        group: "Logo",
        label: "Brand logo",
        description: "The mark for light backgrounds. A surface that is always light draws it (the e-ticket PDF, a light brand's order confirmation email), so a brand that renders dark on the web still needs one",
    },
    {
        key: "logoDark",
        path: "logoDark",
        cssVar: "",
        kind: "asset",
        brandValue: "",
        default: "",
        themeVar: false,
        control: false,
        property: false,
        group: "Logo",
        label: "Dark logo",
        description: "The mark for dark backgrounds, drawn whenever the components render dark",
    },
    {
        key: "navbarLogo",
        path: "navbar.logo",
        cssVar: "",
        kind: "asset",
        brandValue: "",
        default: "",
        themeVar: false,
        control: false,
        property: false,
        group: "Navigation bar",
        label: "Navbar logo",
        description: "Keyed to the surface, not the colour scheme: a navbar can be dark on a light page, which the dark logo cannot express. Unset keeps the scheme-chosen logo",
    },
    {
        key: "footerLogo",
        path: "footer.logo",
        cssVar: "",
        kind: "asset",
        brandValue: "",
        default: "",
        themeVar: false,
        control: false,
        property: false,
        group: "Footer",
        label: "Footer logo",
        description: "Shown in the order confirmation email's footer. Unset keeps the scheme-chosen logo",
    },
];
